import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import chatService from '../../api/chatService';
import useChatSocket from '../../hooks/useChatSocket';
import useAuth from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { relativeTime } from '../../utils/formatters';

function AdminThread({ conversationId, admin }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const endRef = useRef(null);
  const { messages, connected, loading, send } = useChatSocket({
    conversationId,
    senderId: admin.id,
    senderName: t('chat.support'),
    senderType: 'ADMIN',
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { chatService.conversation(conversationId).catch(() => {}); }, [conversationId]); // marks read

  const submit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    send(draft);
    setDraft('');
  };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '');

  return (
    <div className="inbox-thread">
      <div className="chat-head" style={{ borderRadius: 0 }}>
        <span><span className={`chat-dot ${connected ? 'on' : 'off'}`} /> {conversationId}</span>
      </div>
      <div className="chat-body">
        {loading ? (
          <p className="chat-empty">{t('chat.loadingMessages')}</p>
        ) : messages.length === 0 ? (
          <p className="chat-empty">{t('chat.noMessages')}</p>
        ) : (
          messages.map((m, i) => {
            const mine = m.senderType === 'ADMIN';
            return (
              <div key={m.id || i} className={`chat-msg ${mine ? 'mine' : ''}`}>
                <div className="bubble">{m.content}</div>
                <small>{!mine ? `${m.senderName || t('chat.guest')} · ` : ''}{fmt(m.createdAt)}</small>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>
      <form className="chat-input" onSubmit={submit}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t('chat.reply')} />
        <button type="submit" disabled={!draft.trim()}><i className="bi bi-send-fill" /></button>
      </form>
    </div>
  );
}

export default function ChatInbox() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const notify = useNotifications();
  const [convos, setConvos] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    chatService.conversations()
      .then((list) => {
        setConvos(list);
        setSelected((s) => s || list[0]?.conversationId || null);
      })
      .catch((e) => { notify.apiError(e); setConvos([]); });
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  // light polling so new conversations appear without a socket subscription here
  useEffect(() => {
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  if (!convos) return <div className="page"><div className="container"><LoadingSpinner /></div></div>;

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1rem' }}>
        <h1>{t('chat.inboxTitle')}</h1>
        {convos.length === 0 ? (
          <EmptyState icon="chat-dots" title={t('chat.noConversations')} />
        ) : (
          <div className="inbox">
            <div className="inbox-list">
              {convos.map((c) => (
                <button
                  key={c.conversationId}
                  className={selected === c.conversationId ? 'active' : ''}
                  onClick={() => setSelected(c.conversationId)}
                >
                  <span className="who">
                    <span>{c.displayName || t('chat.guest')}</span>
                    {c.unread > 0 && <span className="badge amber">{c.unread}</span>}
                  </span>
                  <span className="prev">{c.lastMessage}</span>
                  <span className="prev" style={{ fontSize: '.72rem' }}>{relativeTime(c.lastAt)}</span>
                </button>
              ))}
            </div>
            {selected ? (
              <AdminThread key={selected} conversationId={selected} admin={{ id: user?.id }} />
            ) : (
              <EmptyState icon="chat-text" title={t('chat.selectConversation')} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
